
import React, { useState, useRef, useEffect } from 'react';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';

const VideoTrimmer = ({ videoSrc, chunkDuration, onClose, onTrimComplete }) => {
  const [ffmpeg] = useState(new FFmpeg());
  const [loaded, setLoaded] = useState(false);
  const [startTime, setStartTime] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [processing, setProcessing] = useState(false);
  const videoRef = useRef(null);
  const [message, setMessage] = useState('Loading FFmpeg...');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      setLoaded(true);
      setMessage('');
    } catch (error) {
      console.error(error);
      setMessage('Failed to load FFmpeg. Please ensure SharedArrayBuffer is enabled.');
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setVideoDuration(videoRef.current.duration);
    }
  };

  const [isPlaying, setIsPlaying] = useState(false);
  const playTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
        if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);
    };
  }, []);

  const handleSliderChange = (value) => {
    // value is [start, end]
    let newStart = value[0];
    // Clamp start time so the window doesn't exceed video duration
    if (newStart + chunkDuration > videoDuration) {
        newStart = Math.max(0, videoDuration - chunkDuration);
    }
    setStartTime(newStart);
    if (videoRef.current) {
      videoRef.current.currentTime = newStart;
      // If user drags, stop any auto-play sequence
      if (isPlaying) {
          videoRef.current.pause();
          setIsPlaying(false);
          if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);
      }
    }
  };

  const togglePlayPreview = () => {
      if (!videoRef.current) return;

      if (isPlaying) {
          videoRef.current.pause();
          setIsPlaying(false);
          if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);
      } else {
          // Start playing from current start time
          videoRef.current.currentTime = startTime;
          videoRef.current.play();
          setIsPlaying(true);

          // Stop after chunk duration
          if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);
          playTimeoutRef.current = setTimeout(() => {
              if (videoRef.current) {
                  videoRef.current.pause();
                  setIsPlaying(false);
                  videoRef.current.currentTime = startTime; // Reset to start
              }
          }, chunkDuration * 1000);
      }
  };

  const handleVideoEnded = () => {
      setIsPlaying(false);
      if (playTimeoutRef.current) clearTimeout(playTimeoutRef.current);
  };
  
  // ... existing trimVideo function ...

  const trimVideo = async () => {
    if (!loaded) return;
    setProcessing(true);
    setMessage('Trimming video...');
      
    try {
      const inputName = 'input.mp4';
      const outputName = 'output.mp4';

      // Fetch file
      await ffmpeg.writeFile(inputName, await fetchFile(videoSrc));

      // Execute command
      await ffmpeg.exec([
        '-ss', startTime.toString(),
        '-i', inputName,
        '-t', chunkDuration.toString(),
        '-c:v', 'libx264',
        '-preset', 'ultrafast',
        '-c:a', 'aac',
        outputName
      ]);

      const data = await ffmpeg.readFile(outputName);
      const blob = new Blob([data.buffer], { type: 'video/mp4' });
        
      onTrimComplete(blob);
    } catch (error) {
      console.error(error);
      setMessage('Error processing video');
    }
    setProcessing(false);
  };

  const endTime = Math.min(startTime + chunkDuration, videoDuration);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-3xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Trim Video Content</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!loaded && <p className="text-center text-gray-500">{message}</p>}

        <div className="mb-4 aspect-video bg-black rounded-lg overflow-hidden flex items-center justify-center relative group">
             <video 
               ref={videoRef}
               src={videoSrc} 
               className="w-full h-full object-contain bg-black"
               onLoadedMetadata={handleLoadedMetadata}
               onEnded={handleVideoEnded}
               controls={false} // Custom controls
               onClick={togglePlayPreview}
             />
             {/* Play Button Overlay */}
             <div 
                className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-200 ${isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}
             >
                 <button 
                    onClick={togglePlayPreview}
                    className="bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full p-4 pointer-events-auto transform transition-transform hover:scale-110"
                 >
                    {isPlaying ? (
                        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                        </svg>
                    ) : (
                        <svg className="w-8 h-8 pl-1" fill="currentColor" viewBox="0 0 24 24">
                             <path d="M8 5v14l11-7z"/>
                        </svg>
                    )}
                 </button>
             </div>
        </div>

        {loaded && (
          <>
            <div className="mb-6 px-4">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Start: {startTime.toFixed(2)}s</span>
                    <span>End: {endTime.toFixed(2)}s</span>
                    <span>Duration: {chunkDuration.toFixed(2)}s</span>
                </div>
              <RangeSlider
                min={0}
                max={videoDuration || 100} // Default to something if duration not loaded yet
                step={0.1}
                value={[startTime, startTime + chunkDuration]} 
                onInput={(value) => handleSliderChange(value)}
                onThumbDragEnd={() => {
                  // Ensure accurate seek after drag
                  if (videoRef.current) {
                      videoRef.current.currentTime = startTime;
                  }
                }}
                thumbsDisabled={[false, true]} 
                rangeSlideDisabled={true} 
              />
            </div>

            <div className="flex justify-end gap-3">
              <button 
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button 
                onClick={trimVideo}
                disabled={processing}
                className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300 flex items-center gap-2`}
              >
                {processing ? (
                    <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                    </>
                ) : 'Trim & Upload'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoTrimmer;
